import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { reportColumnAggregateTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import compact from "lodash/compact";
import mean from "lodash/mean";
import sum from "lodash/sum";
import Tooltip from "rc-tooltip";
import React, { Fragment } from "react";
import { list as logistsList } from "sources/books/employees";
import GroupedByLogistReport from "./groupedByLogistReport";

export default class DriversReport extends GroupedByLogistReport {
    componentDidUpdate() {
        return undefined;
    }

    externalBidColumn = {
        Header: "Внешний ID Заявки",
        accessor: "externalBidId",
        Cell: ({ row: { values } }) => {
            return (values.bids || []).map(({ externalId }, index) => (
                <Fragment key={index}>
                    {externalId}
                    <br />
                </Fragment>
            ));
        },
        Aggregated: () => "",
        filterable: true,
        filter: (rows, _id, filterValue) => {
            if (!filterValue || !filterValue.value) {
                return rows;
            }
            return rows.filter((row) => {
                const value = row.values.bids;
                return Array.isArray(value) && value.some(({ externalId }) => externalId === filterValue.value);
            });
        },
    };

    setTableState = (data) => {
        const params = {};
        let needNewReq = false;

        const newBidFilter = data.filters.find((item) => item.id === "bids");
        const newBidValue = newBidFilter?.value?.value;
        const oldBidFilter = this.state.filters.find((item) => item.id === "bids");
        const oldBidValue = oldBidFilter?.value?.value;
        if (oldBidValue !== newBidValue) {
            needNewReq = true;
            if (newBidValue) {
                params.bidId = Number(newBidValue);
            }
        }

        const newExternalBidFilter = data.filters.find((item) => item.id === "externalBidId");
        const newExternalBidValue = newExternalBidFilter?.value?.value;
        const oldExternalBidFilter = this.state.filters.find((item) => item.id === "externalBidId");
        const oldExternalBidValue = oldExternalBidFilter?.value?.value;
        if (oldExternalBidValue !== newExternalBidValue) {
            needNewReq = true;
            if (newExternalBidValue) {
                params.externalBidId = newExternalBidValue;
            }
        }
        if (needNewReq) {
            this.props.getItem(params);
        }
        this.setState(data);
    };

    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            Cell: (props) => {
                const { car, bids } = props.row.values;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: car };
                switch (props.column.id) {
                    case "driverFullName": {
                        return (
                            <div className="long-text" {...uniqueId}>
                                {formatValue(props.value, column.clrType)} ({bids.length})
                            </div>
                        );
                    }
                    case "carLogistId": {
                        const { subRows } = props.row;
                        const logistName = subRows[0].original?.carLogistDisplayName || "Без логиста";
                        const rowCount = /\(\d+\)$/.test(logistName) ? "" : ` (${subRows.length})`;
                        return (
                            <span data-car-logist-id={props.value}>
                                {logistName}
                                {rowCount}
                            </span>
                        );
                    }
                    case "bids":
                        return (props.value || []).map(
                            (
                                {
                                    id,
                                    hasManualMileage,
                                    hasCarChange,
                                    hasDriverChange,
                                    secondaryDriverFullName,
                                    hasEmulatedMileage,
                                },
                                index,
                            ) => (
                                <Fragment key={index}>
                                    <a
                                        data-bidid={id}
                                        href={paths.bidShow("bid", id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {id}
                                    </a>
                                    {hasManualMileage && (
                                        <>
                                            &nbsp;
                                            <Tooltip overlay="Закрыта вручную">
                                                <i className="icon-uEA83-user-edit b-point__tooltip-icon" />
                                            </Tooltip>
                                        </>
                                    )}
                                    {(hasCarChange || hasDriverChange) && (
                                        <Tooltip
                                            overlay={`Произошла смена ${compact([
                                                hasCarChange ? "машины" : null,
                                                hasDriverChange ? "водителя" : null,
                                            ]).join(" и ")}`}
                                        >
                                            <span className="pl-1 icon-uEAB3-arrows-rotate b-point__tooltip-icon" />
                                        </Tooltip>
                                    )}
                                    {secondaryDriverFullName && (
                                        <Tooltip
                                            overlay={
                                                <div>
                                                    <b>Парный экипаж</b>
                                                    <br />
                                                    Второй водитель: {secondaryDriverFullName}
                                                </div>
                                            }
                                            overlayClassName="notifys__tooltip"
                                            placement="bottom"
                                        >
                                            <i className="icon-uEAC2-user-group b-table__compound-icon pl-1" />
                                        </Tooltip>
                                    )}
                                    {hasEmulatedMileage && (
                                        <Tooltip overlay="В заявке присутствуют сэмулированные участки маршрута">
                                            <i className="pl-1 icon-uEACB-emulated-route b-point__tooltip-icon" />
                                        </Tooltip>
                                    )}
                                    <br />
                                </Fragment>
                            ),
                        );
                    default:
                        return (
                            <div className="long-text" {...uniqueId}>
                                {formatValue(props.value, column.clrType)}
                            </div>
                        );
                }
            },
            ...typeSpecificProps(column),
            aggregate: (leafValues) => {
                if (!this.numericClrTypes.includes(column.clrType)) {
                    return null;
                }
                switch (column.aggregateType) {
                    case reportColumnAggregateTypesEnum.Average:
                        return Math.round(mean(leafValues));
                    case reportColumnAggregateTypesEnum.Sum:
                        return sum(leafValues);
                    default:
                        return null;
                }
            },
            Aggregated: ({ value }) => {
                return value !== null ? <strong>{formatValue(value, column.clrType)}</strong> : "";
            },
            ...(column.propertyName === "CarLogistId"
                ? {
                      Filter: reportFilterWrapper((props) => (
                          <SelectColumnFilter
                              {...props}
                              selectProps={{
                                  ...defaultMultipleSelectProps,
                                  url: logistsList,
                                  inputProps: { name: "carLogistIds" },
                                  propName: "fullNameOrEmail",
                                  components: { ValueContainer },
                              }}
                          />
                      )),
                      filter: (rows, _id, filterValue) => {
                          if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                              return rows;
                          }
                          return rows.filter((row) => filterValue.value.includes(row.values.carLogistId));
                      },
                  }
                : {}),
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        const tableColumns = columns.map((column) => {
            if (column.propertyName === "CarLogistId") {
                column.label = "Логист";
            }
            return this.cellProps(column);
        });
        tableColumns.splice(3, 0, this.externalBidColumn);
        return tableColumns;
    };

    tableExtraProps = () => {
        const { columns } = this.props.data || {};
        const hiddenColumns = columns
            .filter(
                (column) =>
                    column.isAdditionalColumn || column.isFooterData || column.propertyName === "CarLogistDisplayName",
            )
            .map(columnName);
        return {
            initialState: {
                hiddenColumns,
                groupBy: ["carLogistId"],
            },
        };
    };
}
